import { A } from '@ember/array';
import type NativeArray from '@ember/array/-private/native-array';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { get } from '@ember/object';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import findBy from '@nullvoxpopuli/ember-composable-helpers/helpers/find-by';
import pick from '@nullvoxpopuli/ember-composable-helpers/helpers/pick';
import { restartableTask } from 'ember-concurrency';
import set from 'ember-set-helper/helpers/set';
import { eq, or } from 'ember-truth-helpers';
import type GlobalSettingBatch from 'garaje/models/global-setting-batch';
import type Location from 'garaje/models/location';
import { notEmpty } from 'macro-decorators';

interface PropagableSettingsChangedModalLocationsSearcherSignature {
  Args: {
    selectedChildren: NativeArray<Location>;
    globalSettingBatch: GlobalSettingBatch;
    close: () => void;
    nextToConfirm: () => void;
    updateChildren: (locations: Location[]) => void;
  };
}

export default class PropagableSettingsChangedModalLocationsSearcher extends Component<PropagableSettingsChangedModalLocationsSearcherSignature> {
  @service declare store: StoreService;

  @tracked searchQuery: string | null = null;
  @tracked locations: Location[] = [];

  @notEmpty('args.selectedChildren') atLeastOneSelected!: boolean;

  constructor(owner: unknown, args: PropagableSettingsChangedModalLocationsSearcherSignature['Args']) {
    super(owner, args);
    void this.fetchLocations.perform();
  }

  get filteredLocations(): Location[] {
    if (!this.searchQuery) {
      return this.locations;
    }

    return this.locations.filter((location) =>
      location.name.toLowerCase().includes(this.searchQuery?.toLowerCase() || ''),
    );
  }

  get areAllSelected(): boolean {
    return this.locations.length === this.args.selectedChildren.length;
  }

  get selectedMessage(): string {
    const totalLocations = this.locations.length;
    const totalSelected = this.args.selectedChildren.length;

    if (totalSelected === 0) {
      return '';
    } else if (totalSelected === totalLocations) {
      return 'All locations selected';
    } else {
      return `${totalSelected} of ${totalLocations} locations selected`;
    }
  }

  fetchLocations = restartableTask(async () => {
    // eslint-disable-next-line ember/no-get
    const locationParentId = get(this.args.globalSettingBatch, 'parent.id');
    const locations = await this.store.findAll('location');

    // Remove current location from the list
    this.locations = locations.filter((location) => {
      return location.id !== locationParentId;
    });
  });

  toggleAllSelected = (): void => {
    if (this.areAllSelected) {
      this.args.updateChildren(A());
    } else {
      this.args.updateChildren(this.locations.slice());
    }
  };

  toggleLocation = (location: Location): void => {
    const selectedChildren = this.args.selectedChildren;
    if (selectedChildren.includes(location)) {
      selectedChildren.removeObject(location);
    } else {
      selectedChildren.addObject(location);
    }
  };

  <template>
    <div data-test-propagable-locations-selector class="propagable--locations-selector">
      <header>
        <h2 class="mb-8">
          <span data-test-selector-title>Select Locations</span>
          <button {{on "click" @close}} class="btn btn--small btn--outline btn--cancel float-right box-border">
            &times;
          </button>
        </h2>
        <input
          type="search"
          data-test-searcher-search
          value={{this.searchQuery}}
          class="pageSearch rounded-lg border icon-search"
          placeholder="Search locations..."
          {{on "input" (pick "target.value" (set this "searchQuery"))}}
        />
        <div class="search-options">
          {{#unless this.fetchLocations.isRunning}}
            <input
              type="checkbox"
              data-test-toggle-all
              {{on "click" this.toggleAllSelected}}
              checked={{this.areAllSelected}}
            />
          {{/unless}}
          <div class="selected-message" data-test-selected-message>{{this.selectedMessage}}</div>
        </div>
      </header>
      <div
        data-test-locations-list
        class="locations-list
          {{if (or this.fetchLocations.isRunning (eq this.filteredLocations.length 0)) 'text-centered'}}"
      >
        {{#each this.filteredLocations as |location|}}
          {{#let (findBy "id" location.id @selectedChildren) as |isSelected|}}
            <div
              data-test-location-row
              class="location-row {{if isSelected 'is-selected'}}"
              {{on "click" (fn this.toggleLocation location)}}
            >
              <input type="checkbox" checked={{if isSelected true false}} />
              <span class="row--name" data-test-row-name>{{location.name}}</span>
            </div>
          {{/let}}
        {{else}}
          <div data-test-no-match class="locations-list--no-match-locations py-4">
            {{#if this.fetchLocations.isRunning}}
              Loading...
            {{else}}
              No match found.
            {{/if}}
          </div>
        {{/each}}
      </div>
      <footer class="!p-4 !h-auto text-right">
        <button
          data-test-next-to-confirm
          {{on "click" @nextToConfirm}}
          class="btn btn--brand box-border {{unless this.atLeastOneSelected 'disabled'}}"
        >
          Next
        </button>
      </footer>
    </div>
  </template>
}
